import { useState } from 'react';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Upload, X, FileText, Image, ShieldCheck, Calculator } from 'lucide-react';
import { SubmitDealStepProps, UploadedFiles } from '@/types/submit-deal.types';

interface StepDocumentsProps extends SubmitDealStepProps {
  uploadedFiles: UploadedFiles;
  onFileUpload: (type: 'documents' | 'photos' | 'logo', files: FileList) => void;
  onFileRemove: (type: 'documents' | 'photos' | 'logo', index?: number) => void;
}

// AJOUTÉ — hypothèses de financement conditionnelles selon le pays du
// deal, plutôt qu'une seule hypothèse américaine appliquée partout.
//   - Canada : 75% LTV, 7,0%, 10 ans — prêts conventionnels bancaires/BDC,
//     mise de fonds typique 20-35% (milieu ~25%), taux ~6-8% début 2026
//   - États-Unis : 80% LTV, 9,0%, 10 ans — SBA 7(a), médiane 9,50%/
//     moyenne 9,31% sur >88 000 prêts d'acquisition approuvés en 2025-2026
//   - Autre / non reconnu : repli sur l'hypothèse américaine (la mieux
//     documentée), avec mention explicite que ce n'est qu'un repli
// Le terme (10 ans) est identique dans les deux cas — aucune donnée
// fiable trouvée indiquant un terme standard différent au Canada (BDC
// parle de remboursement "calqué sur les flux de trésorerie" plutôt
// qu'un chiffre fixe) ; à ajuster si une meilleure donnée est disponible.
function getFinancingAssumptions(locationCountry: string | undefined) {
  const c = (locationCountry || '').toLowerCase();
  if (c.includes('canada')) {
    return { ltv: 75, rate: 7.0, termYears: 10, region: 'Canada' as const };
  }
  if (c.includes('united states') || c.includes('usa') || c.includes('états-unis') || c.includes('etats-unis')) {
    return { ltv: 80, rate: 9.0, termYears: 10, region: 'United States' as const };
  }
  return { ltv: 80, rate: 9.0, termYears: 10, region: 'default' as const };
}

export const StepDocuments = ({ 
  formData, 
  updateFormData, 
  errors, 
  clearError, 
  t,
  uploadedFiles,
  onFileUpload,
  onFileRemove
}: StepDocumentsProps) => {

  const handleAnonymizationChange = (field: string, value: boolean) => {
    updateFormData('documents', {
      publicAnonymizationFlags: {
        ...formData.documents.publicAnonymizationFlags,
        [field]: value
      }
    });
  };

  // AJOUTÉ — estimation DSCR automatique, non vérifiée, affichée AVANT que
  // le broker/vendeur paie les frais de pré-évaluation (300 $), pour qu'il
  // ne paie pas à l'aveugle sur une plateforme qui annonce publiquement
  // n'accepter que les deals avec un potentiel DSCR ≥ 1,3x. Calcul
  // déterministe, pas un appel IA.
  //
  // CORRIGÉ — le calcul réel (monthlyRate) était resté à 0.075 (7,5%)
  // malgré un commentaire disant 9,0% lors du correctif précédent — la
  // constante elle-même et le texte affiché n'avaient pas été mis à jour,
  // seul le commentaire l'avait été. Corrigé ici, et remplacé par des
  // hypothèses conditionnelles au pays du deal (voir
  // getFinancingAssumptions ci-dessus) plutôt qu'une valeur unique.
  const financials = formData.financials || ({} as any);
  const addbacks = formData.addbacks || ({} as any);
  const currency = formData.general?.currency || 'USD';
  const assumptions = getFinancingAssumptions(formData.general?.locationCountry);

  const ebitdaYears = [financials.ebitdaYear1, financials.ebitdaYear2, financials.ebitdaYear3]
    .filter((v) => typeof v === 'number' && v > 0);
  const ebitdaAvg = ebitdaYears.length ? ebitdaYears.reduce((a, b) => a + b, 0) / ebitdaYears.length : 0;
  const addbacksTotal = (addbacks.ownerSalary || 0) + (addbacks.autoExpense || 0)
    + (addbacks.travelEntertainment || 0) + (addbacks.nonRecurringLegal || 0)
    + (addbacks.otherDiscretionary || 0);
  const adjustedEbitdaEstimate = ebitdaAvg + addbacksTotal;

  const rawAskingPrice = financials.includeRealEstateInPrice
    ? (financials.totalAskingPriceWithRealEstate || financials.askingPrice)
    : financials.askingPrice;
  const askingPriceNum = Number(rawAskingPrice) || 0;

  const loanAmount = askingPriceNum * (assumptions.ltv / 100);
  const monthlyRate = (assumptions.rate / 100) / 12;
  const nPayments = assumptions.termYears * 12;
  const annualDebtService = loanAmount > 0
    ? (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, nPayments))
      / (Math.pow(1 + monthlyRate, nPayments) - 1) * 12
    : 0;
  const hasEnoughData = askingPriceNum > 0 && adjustedEbitdaEstimate > 0 && annualDebtService > 0;
  const dscrEstimate = hasEnoughData ? adjustedEbitdaEstimate / annualDebtService : null;
  const meetsThreshold = dscrEstimate !== null && dscrEstimate >= 1.3;

  const fmtMoney = (v: number) => new Intl.NumberFormat('fr-CA', { style: 'currency', currency, minimumFractionDigits: 0 }).format(v);

  const methodologyLabel = assumptions.region === 'Canada'
    ? 'hypothèses canadiennes — prêt bancaire/BDC conventionnel'
    : assumptions.region === 'United States'
      ? 'hypothèses américaines — SBA 7(a)'
      : 'hypothèses génériques par défaut (pays non reconnu)';

  return (
    <div className="space-y-6">
      <div className="bg-muted/50 p-4 rounded-lg">
        <p className="text-sm text-muted-foreground">{t.step11.sectionHint}</p>
      </div>

      {/* AJOUTÉ — estimation DSCR précoce, non vérifiée. */}
      <div
        className="p-4 rounded-lg border-2"
        style={{
          borderColor: hasEnoughData ? (meetsThreshold ? '#639922' : '#BA7517') : '#94a3b8',
          background: hasEnoughData ? (meetsThreshold ? 'rgba(99,153,34,0.06)' : 'rgba(186,117,23,0.06)') : 'rgba(148,163,184,0.08)',
        }}
      >
        <div className="flex items-start gap-3">
          <Calculator className="h-5 w-5 shrink-0 mt-0.5" style={{ color: hasEnoughData ? (meetsThreshold ? '#639922' : '#BA7517') : '#64748b' }} />
          <div className="flex-1 space-y-1.5">
            <p className="font-semibold text-sm" style={{ color: '#1D314D' }}>
              Estimation DSCR (Debt Service Coverage Ratio)
            </p>
            {!hasEnoughData ? (
              <p className="text-sm text-muted-foreground">
                Complétez le prix demandé (étape 6) et l'EBITDA des 3 dernières années (étapes 6-7) pour voir une estimation.
              </p>
            ) : (
              <>
                <p className="text-2xl font-bold" style={{ color: meetsThreshold ? '#3B6D11' : '#854F0B' }}>
                  {dscrEstimate!.toFixed(2)}x
                  <span className="text-sm font-normal text-muted-foreground ml-2">
                    (seuil minimum AcquizGroup™ : 1,3x)
                  </span>
                </p>
                <p className="text-xs text-muted-foreground">
                  Basé sur un prêt conventionnel standard ({assumptions.ltv} % du prix demandé, taux {assumptions.rate.toFixed(1)} %,
                  terme {assumptions.termYears} ans — {methodologyLabel}) et votre EBITDA ajusté estimé de {fmtMoney(adjustedEbitdaEstimate)} (moyenne
                  3 ans + redressements déclarés).
                </p>
                {!meetsThreshold && (
                  <p className="text-xs font-medium" style={{ color: '#854F0B' }}>
                    ⚠ Ce résultat est sous le seuil minimum généralement accepté. Le paiement des frais de pré-évaluation
                    n'entraîne pas automatiquement l'approbation du deal.
                  </p>
                )}
              </>
            )}
            <p className="text-[11px] text-muted-foreground italic pt-1 border-t mt-2">
              ⚠ Estimation automatique, non vérifiée — le staff AcquizGroup™ confirmera ce calcul lors de la pré-évaluation
              réelle (DSCR Evaluator). Ce chiffre sert uniquement d'indicateur préliminaire avant de soumettre votre dossier.
            </p>
          </div>
        </div>
      </div>

      {/* CORRIGÉ — transformé de case à cocher en simple affirmation
          permanente. L'ancienne version (précochée par défaut mais
          toujours désactivable) restait un point d'oubli possible — sans
          indicateur visible côté staff pour repérer un deal où elle
          aurait été décochée par erreur. Plutôt que d'ajouter un rappel
          visuel supplémentaire, la question elle-même est retirée :
          formData.documents.lendersShareConsent reste fixé à true en
          permanence (voir createInitialFormData()), affiché ici comme un
          fait acquis du processus plutôt que comme un choix. */}
      <div
        className="flex items-start gap-3 p-4 rounded-lg border-2"
        style={{ borderColor: '#CEAB65', background: 'rgba(206,171,101,0.08)' }}
      >
        <ShieldCheck className="h-6 w-6 shrink-0 mt-0.5" style={{ color: '#CEAB65' }} />
        <div className="flex-1 space-y-1">
          <p className="font-semibold text-base" style={{ color: '#1D314D' }}>
            {t.step11.lendersShareConsent}
          </p>
          <p className="text-sm text-muted-foreground">{t.step11.lendersShareConsentHint}</p>
        </div>
      </div>

      <div className="border-t pt-6">
        <h4 className="text-lg font-medium mb-4">{t.step11.documentUpload}</h4>
        
        {/* Financial Documents */}
        <div className="space-y-4 mb-6">
          <Label>{t.step11.financialDocuments}</Label>
          <p className="text-xs text-muted-foreground">{t.step11.financialDocumentsHint}</p>
          
          <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center">
            <input
              type="file"
              id="documents"
              multiple
              accept=".pdf,.doc,.docx,.xls,.xlsx"
              className="hidden"
              onChange={(e) => e.target.files && onFileUpload('documents', e.target.files)}
            />
            <label htmlFor="documents" className="cursor-pointer">
              <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">Click to upload or drag and drop</p>
            </label>
          </div>

          {uploadedFiles.documents.length > 0 && (
            <div className="space-y-2">
              {uploadedFiles.documents.map((file, index) => (
                <div key={index} className="flex items-center justify-between bg-muted/50 p-3 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{file.name}</span>
                    <span className="text-xs text-muted-foreground">({(file.size / 1024).toFixed(1)} KB)</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onFileRemove('documents', index)}
                  >
                    <X className="h-4 w-4" />
                    <span className="sr-only">{t.step11.remove}</span>
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Business Photos */}
        <div className="space-y-4 mb-6">
          <Label>{t.step11.businessPhotos}</Label>
          
          <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center">
            <input
              type="file"
              id="photos"
              multiple
              accept="image/*"
              className="hidden"
              onChange={(e) => e.target.files && onFileUpload('photos', e.target.files)}
            />
            <label htmlFor="photos" className="cursor-pointer">
              <Image className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">Click to upload photos</p>
            </label>
          </div>

          {uploadedFiles.photos.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {uploadedFiles.photos.map((file, index) => (
                <div key={index} className="relative group">
                  <img src={file.url} alt={file.name} className="w-full h-24 object-cover rounded-lg" />
                  <Button
                    variant="destructive"
                    size="icon"
                    className="absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => onFileRemove('photos', index)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Business Logo */}
        <div className="space-y-4">
          <Label>{t.step11.businessLogo}</Label>
          <p className="text-xs text-muted-foreground">{t.step11.businessLogoHint}</p>
          
          <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center">
            <input
              type="file"
              id="logo"
              accept="image/*"
              className="hidden"
              onChange={(e) => e.target.files && onFileUpload('logo', e.target.files)}
            />
            <label htmlFor="logo" className="cursor-pointer">
              {uploadedFiles.logo ? (
                <div className="relative inline-block">
                  <img src={uploadedFiles.logo.url} alt="Logo" className="h-24 w-24 object-cover rounded-lg mx-auto" />
                  <Button
                    variant="destructive"
                    size="icon"
                    className="absolute -top-2 -right-2 h-6 w-6"
                    onClick={(e) => {
                      e.preventDefault();
                      onFileRemove('logo');
                    }}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ) : (
                <>
                  <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">Click to upload logo</p>
                </>
              )}
            </label>
          </div>
        </div>
      </div>

      <div className="border-t pt-6">
        <h4 className="text-lg font-medium mb-4">{t.step11.anonymization}</h4>
        <div className="space-y-3">
          <div className="flex items-center space-x-3">
            <Checkbox
              id="hideLegalName"
              checked={formData.documents.publicAnonymizationFlags.hideLegalName}
              onCheckedChange={(checked) => handleAnonymizationChange('hideLegalName', checked as boolean)}
            />
            <Label htmlFor="hideLegalName" className="font-normal cursor-pointer">
              {t.step11.hideLegalName}
            </Label>
          </div>
          <div className="flex items-center space-x-3">
            <Checkbox
              id="hideExactLocation"
              checked={formData.documents.publicAnonymizationFlags.hideExactLocation}
              onCheckedChange={(checked) => handleAnonymizationChange('hideExactLocation', checked as boolean)}
            />
            <Label htmlFor="hideExactLocation" className="font-normal cursor-pointer">
              {t.step11.hideExactLocation}
            </Label>
          </div>
          <div className="flex items-center space-x-3">
            <Checkbox
              id="hideOwnerName"
              checked={formData.documents.publicAnonymizationFlags.hideOwnerName}
              onCheckedChange={(checked) => handleAnonymizationChange('hideOwnerName', checked as boolean)}
            />
            <Label htmlFor="hideOwnerName" className="font-normal cursor-pointer">
              {t.step11.hideOwnerName}
            </Label>
          </div>
        </div>
      </div>
    </div>
  );
};